import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import LandingBottomNav from "@/components/LandingBottomNav";
import { UploadZone } from "@/components/UploadZone";
import { ProcessingOverlay } from "@/components/ProcessingOverlay";
import UsageBadge from "@/components/UsageBadge";
import UpgradeModal from "@/components/UpgradeModal";
import WelcomeBackBanner from "@/components/WelcomeBackBanner";
import { useAuth } from "@/contexts/AuthContext";
import { uploadPdfs, uploadPdfsBatched, fetchUsage, pollReportUntilReady } from "@/lib/api";

/**
 * Dedicated /upload page — hosts the upload zone that used to live on the
 * landing page. Accessed from the UPLOAD tab in the bottom nav. Mirrors the
 * upload-related logic from Landing.jsx so the upload experience stays
 * identical; removed for the Home scroll to keep that page purely a pitch.
 */
export default function UploadPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [phone, setPhone] = useState("");
  const [processing, setProcessing] = useState(false);
  const [usage, setUsage] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);

  useEffect(() => {
    if (user?.phone && !phone) setPhone(user.phone);
  }, [user, phone]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchUsage(phone.trim() || null);
        if (!cancelled) setUsage(data);
      } catch {
        /* silent */
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [phone]);

  const hasFiles = useMemo(() => files.length > 0, [files]);

  const onAnalyse = async () => {
    if (!files.length) {
      toast.error("Add at least one PDF invoice or order screenshot to analyse.");
      return;
    }
    const rawPhone = phone.trim();
    if (rawPhone) {
      const digits = rawPhone.replace(/[\s-]/g, "");
      const validIN = /^(\+?91|0)?[6-9]\d{9}$/.test(digits);
      if (!validIN) {
        toast.error("Enter a valid 10-digit mobile number.");
        return;
      }
    }
    const authedPlan = (user?.plan || "").toLowerCase();
    const isPaidPlan = authedPlan && authedPlan !== "free";
    if (
      !isPaidPlan &&
      usage &&
      usage.plan === "free" &&
      usage.used + files.length > usage.limit
    ) {
      setUpgradeOpen(true);
      return;
    }
    setProcessing(true);
    setBatchProgress(null);
    try {
      const BATCH_THRESHOLD = 3;
      if (files.length > BATCH_THRESHOLD) {
        toast.info(`Optimising ${files.length} files for upload…`);
        const final = await uploadPdfsBatched({
          files,
          phone: phone.trim() || null,
          budget: 9000,
          batchSize: BATCH_THRESHOLD,
          onCompressionSummary: ({ savedPct, savedBytes, fileCount }) => {
            if (savedBytes > 100 * 1024) {
              const mb = (savedBytes / (1024 * 1024)).toFixed(1);
              toast.success(
                `Compressed ${fileCount} screenshots — saved ${mb} MB (${savedPct}%) before upload.`
              );
            }
          },
          onProgress: ({ batch, totalBatches, status }) => {
            setBatchProgress({ batch, totalBatches, status });
          },
        });
        if (!final?.reportId) {
          toast.error("Upload finished but no report came back. Try again.");
          return;
        }
        // P1-19: surface the nutrition_score + anomaly badge in the success
        // toast so the user gets a wellness signal without needing to open
        // the dashboard first. Silent when we have no nutrition data yet.
        showNutritionToast(final.ready?.reportData || final.ready);
        navigate(`/dashboard/${final.reportId}`, {
          state: {
            reportData: final.ready?.reportData || final.ready,
            invoiceHistory: final.ready?.invoiceHistory || [],
            userId: final.ready?.userId,
            phone: phone.trim() || null,
          },
        });
        return;
      }

      const data = await uploadPdfs({
        files,
        phone: phone.trim() || null,
        budget: 9000,
      });
      if (data?.reportId) {
        let final = data;
        if (data?.reportData?.status === "processing") {
          try {
            final = await pollReportUntilReady(data.reportId, {
              intervalMs: 2500,
              timeoutMs: 5 * 60 * 1000,
            });
          } catch (pollErr) {
            toast.error(pollErr?.message || "Report generation failed.");
            return;
          }
        }
        showNutritionToast(final.reportData);
        navigate(`/dashboard/${final.reportId || data.reportId}`, {
          state: {
            reportData: final.reportData,
            invoiceHistory: final.invoiceHistory || [],
            userId: final.userId,
            phone: phone.trim() || null,
          },
        });
      } else {
        toast.error("Something went wrong. Please retry.");
      }
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 402 && detail?.error === "upload_limit_reached") {
        setUsage((u) => ({
          ...(u || {}),
          used: detail.used ?? u?.used,
          limit: detail.limit ?? u?.limit,
          plan: detail.plan ?? u?.plan ?? "free",
        }));
        setUpgradeOpen(true);
      } else if (status === 502 || status === 413) {
        toast.error(
          "That batch was too large for the upload gateway. Try uploading fewer files at once or smaller screenshots."
        );
      } else {
        toast.error(
          typeof detail === "string" ? detail : detail?.message || "Upload failed. Try again."
        );
      }
    } finally {
      setProcessing(false);
      setBatchProgress(null);
    }
  };

  return (
    <div
      className="cartiq-wellness min-h-screen bg-[#F8FAFC] text-[#334155] relative pb-20 md:pb-0"
      data-testid="upload-page"
    >
      <Toaster position="top-center" richColors theme="light" />
      {processing && <ProcessingOverlay batch={batchProgress} />}
      {upgradeOpen && (
        <UpgradeModal
          used={usage?.used ?? 0}
          limit={usage?.limit ?? 5}
          onClose={() => setUpgradeOpen(false)}
          onUpgrade={() => {
            setUpgradeOpen(false);
            navigate("/pricing");
          }}
        />
      )}

      <header className="border-b border-[#2A2A35] bg-[#0A0A0F]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <BrandMark />
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <Link
              to="/"
              className="text-[10px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] font-bold px-2 sm:px-3 py-1.5 sm:py-2 border border-[#2A2A35] text-[#888] hover:text-[#F0EDE8] hover:border-[#F0EDE8] transition-colors whitespace-nowrap inline-flex items-center gap-1.5"
              data-testid="upload-back-link"
            >
              <ArrowLeft className="w-3 h-3" />
              <span className="hidden sm:inline">Back to home</span>
              <span className="sm:hidden">Back</span>
            </Link>
            <GoogleAuthButton target="/upload" />
          </div>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 pt-10 md:pt-14 pb-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#FF6B00] text-[#FF6B00] text-[11px] uppercase tracking-[0.22em] font-bold">
            Upload
          </div>
          <h1
            className="font-display uppercase mt-5 text-[clamp(32px,5vw,52px)] leading-[1.05] tracking-tight"
            data-testid="upload-page-heading"
          >
            Drop your invoices &amp; <span className="text-[#FF6B00]">see the truth.</span>
          </h1>
          <p className="mt-3 text-sm md:text-base text-[#888] max-w-xl mx-auto">
            PDFs or order screenshots from Swiggy, Zomato, Blinkit, Zepto, BigBasket and
            every other Indian food or grocery app.
          </p>
        </div>

        <WelcomeBackBanner />

        <div
          className="cartiq-card !p-6 md:!p-10 relative overflow-hidden"
          data-testid="upload-section"
        >
          <div className="accent-bar absolute top-0 left-0 right-0" />
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold">
              — Start now
            </div>
            {usage && (
              <UsageBadge used={usage.used} limit={usage.limit} plan={usage.plan} />
            )}
          </div>

          <UploadZone files={files} setFiles={setFiles} disabled={processing} />

          <div className="mt-6 grid md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-[#888] font-bold block mb-1.5">
                WhatsApp number (optional)
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="cartiq-input w-full font-mono-num"
                data-testid="phone-input"
              />
              <div className="text-xs text-[#555566] mt-1.5">
                Save your report and unlock WhatsApp weekly reminders.
              </div>
            </div>
            <button
              type="button"
              className="btn-saffron flex items-center justify-center gap-2 h-12 md:w-auto w-full"
              disabled={processing || !hasFiles}
              onClick={onAnalyse}
              data-testid="analyse-btn"
            >
              {processing ? "Analysing…" : "Analyse my orders"}
              {!processing && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </section>

      <LandingBottomNav />
    </div>
  );
}

// P1-19 · Nutrition toast helper. Fires after a successful upload, but only
// when the report actually carries nutrition data. Renders a compact
// "Nutrition score 78 · 2 items flagged" line under the default success
// message. Silent when nutrition_summary is empty (Open Food Facts missed
// every item — no signal to share).
function showNutritionToast(reportData) {
  const ns = reportData?.nutritionSummary;
  if (!ns || !ns.items_with_data) return;
  const score = ns.nutrition_score ?? 0;
  const anom = (ns.anomalies || []).length;
  const scoreTone =
    score >= 75 ? "text-[#3B6D11]" : score >= 50 ? "text-[#BA7517]" : "text-[#E11D48]";
  toast.success(
    (
      <div className="text-[13px]" data-testid="nutrition-upload-toast">
        <div className="font-semibold text-[#0F172A]">Report ready.</div>
        <div className="mt-0.5 text-[#64748B]">
          Nutrition score{" "}
          <span className={`font-extrabold ${scoreTone}`}>{score}/100</span>
          {anom > 0 ? (
            <>
              {" "}·{" "}
              <span
                className="font-semibold text-[#E11D48]"
                data-testid="nutrition-upload-toast-anomalies"
              >
                {anom} item{anom > 1 ? "s" : ""} flagged
              </span>
            </>
          ) : (
            <>
              {" "}· <span className="text-[#3B6D11] font-medium">all clear</span>
            </>
          )}
        </div>
      </div>
    ),
    { duration: 5000 },
  );
}
